import { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import '../pm/theme.css';
import PmSuiteLayout from '../components/pm/PmSuiteLayout';
import {
  ProjectPriority,
  ProjectTemplate,
  TemplateTask,
  TemplateWorkstream,
} from '../pm/types';
import {
  createProjectFromTemplate,
  createTemplateTask,
  createTemplateWorkstream,
  deleteProjectTemplate,
  deleteTemplateTask,
  deleteTemplateWorkstream,
  subscribeProjectTemplates,
  subscribeTemplateTasks,
  subscribeTemplateWorkstreams,
  updateProjectTemplate,
  updateTemplateTask,
  updateTemplateWorkstream,
} from '../pm/firebase';
import { linkify } from '../pm/linkify';
import { getCurrentUser } from '../firebase';
import UseTemplateModal from '../components/pm/UseTemplateModal';

// Editable scaffold for a single project template. No statuses, owners,
// comments, or due dates — just label/description and an optional relative
// day-offset that the materializer turns into a concrete dueDate.
const TemplateDetail: React.FC = () => {
  const history = useHistory();
  const { templateId } = useParams<{ templateId: string }>();

  const [uid, setUid] = useState<string>('');
  const [authLoading, setAuthLoading] = useState(true);
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);
  const [workstreams, setWorkstreams] = useState<TemplateWorkstream[]>([]);
  const [tasks, setTasks] = useState<TemplateTask[]>([]);

  // Local edit-state for header fields so typing isn't slowed by the realtime
  // round-trip. We commit on blur.
  const [titleDraft, setTitleDraft] = useState<string | null>(null);
  const [descDraft, setDescDraft] = useState<string | null>(null);

  const [newWorkstreamTitle, setNewWorkstreamTitle] = useState('');
  const [addingWs, setAddingWs] = useState(false);
  const [newTaskByWs, setNewTaskByWs] = useState<Record<string, string>>({});

  const [useOpen, setUseOpen] = useState(false);
  const [useBusy, setUseBusy] = useState(false);
  const [useError, setUseError] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getCurrentUser()
      .then((u: any) => {
        setUid(u?.uid || '');
        setAuthLoading(false);
      })
      .catch(() => setAuthLoading(false));
  }, []);

  useEffect(() => {
    const unsubT = subscribeProjectTemplates(setTemplates);
    const unsubW = subscribeTemplateWorkstreams(setWorkstreams);
    const unsubTk = subscribeTemplateTasks(setTasks);
    return () => {
      unsubT();
      unsubW();
      unsubTk();
    };
  }, []);

  const template = useMemo(
    () => templates.find((t) => t.id === templateId) || null,
    [templates, templateId],
  );

  const templateWorkstreams = useMemo(
    () =>
      workstreams
        .filter((w) => w.templateId === templateId)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [workstreams, templateId],
  );

  const tasksByWs = useMemo(() => {
    const m: Record<string, TemplateTask[]> = {};
    for (const t of tasks) {
      if (t.templateId !== templateId) continue;
      if (!m[t.workstreamId]) m[t.workstreamId] = [];
      m[t.workstreamId].push(t);
    }
    for (const key of Object.keys(m)) {
      m[key].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    }
    return m;
  }, [tasks, templateId]);

  async function commitTitle() {
    if (!template || titleDraft === null) return;
    const next = titleDraft.trim();
    setTitleDraft(null);
    if (!next || next === template.title) return;
    try {
      await updateProjectTemplate(template.id, { title: next }, uid);
    } catch (err: any) {
      setError(err?.message || 'Could not save title.');
    }
  }

  async function commitDescription() {
    if (!template || descDraft === null) return;
    const next = descDraft.trim();
    setDescDraft(null);
    if (next === (template.description || '')) return;
    try {
      await updateProjectTemplate(template.id, { description: next }, uid);
    } catch (err: any) {
      setError(err?.message || 'Could not save description.');
    }
  }

  async function handleAddWorkstream(e: React.FormEvent) {
    e.preventDefault();
    if (!template) return;
    const title = newWorkstreamTitle.trim();
    if (!title) return;
    setAddingWs(true);
    setError(null);
    try {
      const nextOrder =
        (templateWorkstreams[templateWorkstreams.length - 1]?.sortOrder ?? 0) + 1;
      await createTemplateWorkstream(template.id, title, nextOrder);
      setNewWorkstreamTitle('');
    } catch (err: any) {
      setError(err?.message || 'Could not add workstream.');
    } finally {
      setAddingWs(false);
    }
  }

  async function handleRenameWorkstream(ws: TemplateWorkstream, next: string) {
    const trimmed = next.trim();
    if (!trimmed || trimmed === ws.title) return;
    try {
      await updateTemplateWorkstream(ws.id, { title: trimmed });
    } catch (err: any) {
      setError(err?.message || 'Could not rename workstream.');
    }
  }

  async function handleDeleteWorkstream(ws: TemplateWorkstream) {
    const owned = tasks.filter((t) => t.workstreamId === ws.id);
    if (owned.length > 0) {
      const confirmed = window.confirm(
        `Delete workstream "${ws.title}" and its ${owned.length} task${owned.length === 1 ? '' : 's'}?`,
      );
      if (!confirmed) return;
    }
    try {
      await deleteTemplateWorkstream(ws.id, tasks);
    } catch (err: any) {
      setError(err?.message || 'Could not delete workstream.');
    }
  }

  async function handleAddTask(ws: TemplateWorkstream) {
    if (!template) return;
    const label = (newTaskByWs[ws.id] || '').trim();
    if (!label) return;
    try {
      const current = tasksByWs[ws.id] || [];
      const nextOrder = (current[current.length - 1]?.sortOrder ?? 0) + 1;
      await createTemplateTask({
        templateId: template.id,
        workstreamId: ws.id,
        label,
        sortOrder: nextOrder,
      });
      setNewTaskByWs((prev) => ({ ...prev, [ws.id]: '' }));
    } catch (err: any) {
      setError(err?.message || 'Could not add task.');
    }
  }

  async function handleRenameTask(task: TemplateTask, next: string) {
    const trimmed = next.trim();
    if (!trimmed || trimmed === task.label) return;
    try {
      await updateTemplateTask(task.id, { label: trimmed });
    } catch (err: any) {
      setError(err?.message || 'Could not rename task.');
    }
  }

  async function handleEditTaskDescription(task: TemplateTask, next: string) {
    const trimmed = next.trim();
    if (trimmed === (task.description || '')) return;
    try {
      await updateTemplateTask(task.id, { description: trimmed });
    } catch (err: any) {
      setError(err?.message || 'Could not save description.');
    }
  }

  async function handleChangeOffset(task: TemplateTask, raw: string) {
    if (raw === '') {
      try { await updateTemplateTask(task.id, { dueOffsetDays: null }); }
      catch (err: any) { setError(err?.message || 'Could not save offset.'); }
      return;
    }
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    try {
      await updateTemplateTask(task.id, { dueOffsetDays: Math.round(n) });
    } catch (err: any) {
      setError(err?.message || 'Could not save offset.');
    }
  }

  async function handleDeleteTask(task: TemplateTask) {
    try {
      await deleteTemplateTask(task.id);
    } catch (err: any) {
      setError(err?.message || 'Could not delete task.');
    }
  }

  async function handleDeleteTemplate() {
    if (!template) return;
    const confirmed = window.confirm(
      `Delete template "${template.title}"?\nThis can't be undone.`,
    );
    if (!confirmed) return;
    try {
      await deleteProjectTemplate(template.id, workstreams, tasks);
      history.push('/Projects/Templates');
    } catch (err: any) {
      setError(err?.message || 'Could not delete template.');
    }
  }

  async function handleUseSubmit(input: {
    title: string;
    description: string;
    priority?: ProjectPriority;
    startDate?: number;
  }) {
    if (!template) return;
    setUseBusy(true);
    setUseError(null);
    try {
      const newProjectId = await createProjectFromTemplate(
        template.id,
        input,
        workstreams,
        tasks,
        uid,
      );
      setUseOpen(false);
      history.push(`/Projects/${newProjectId}`);
    } catch (err: any) {
      setUseError(err?.message || 'Could not create project from template.');
    } finally {
      setUseBusy(false);
    }
  }

  if (authLoading) {
    return (
      <PmSuiteLayout
        active="templates"
        onSectionChange={(id) => {
          // Sub-rail click from TemplateDetail → cross-route navigation.
          if (id === 'mywork') history.push('/Projects/MyWork');
          else if (id === 'projects') history.push('/Projects');
          else history.push('/Projects/Templates');
        }}
      >
        <div className="pm-loading">Loading…</div>
      </PmSuiteLayout>
    );
  }

  if (!template) {
    return (
      <PmSuiteLayout
        active="templates"
        onSectionChange={(id) => {
          // Sub-rail click from TemplateDetail → cross-route navigation.
          if (id === 'mywork') history.push('/Projects/MyWork');
          else if (id === 'projects') history.push('/Projects');
          else history.push('/Projects/Templates');
        }}
      >
        <div className="pm-page">
          <div className="pm-breadcrumb">
            <button
              type="button"
              className="pm-link-button"
              onClick={() => history.push('/Projects/Templates')}
            >
              Templates
            </button>
          </div>
          <div className="pm-empty">
            <h3>Template not found</h3>
            <p>It may have been deleted.</p>
            <button
              className="pm-button"
              onClick={() => history.push('/Projects/Templates')}
            >
              Back to templates
            </button>
          </div>
        </div>
      </PmSuiteLayout>
    );
  }

  return (
    <PmSuiteLayout
      active="templates"
      onSectionChange={(id) => {
        if (id === 'mywork') history.push('/Projects/MyWork');
        else if (id === 'projects') history.push('/Projects');
        else history.push('/Projects/Templates');
      }}
    >
      <div className="pm-page">
        <div className="pm-breadcrumb">
          <div className="pm-breadcrumb-trail">
                <button
                  type="button"
                  className="pm-link-button"
                  onClick={() => history.push('/Projects/Templates')}
                >
                  Templates
                </button>
                {' / '}
                <span>{template.title}</span>
              </div>
            </div>

            <div className="pm-header">
              <div className="pm-header-titles">
                {titleDraft === null ? (
                  <h1
                    className="pm-template-title-display"
                    title="Click to rename"
                    onClick={() => setTitleDraft(template.title)}
                  >
                    {linkify(template.title)}
                  </h1>
                ) : (
                  <input
                    className="pm-template-title-input"
                    value={titleDraft}
                    autoFocus
                    onChange={(e) => setTitleDraft(e.target.value)}
                    onBlur={commitTitle}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); commitTitle(); }
                      else if (e.key === 'Escape') setTitleDraft(null);
                    }}
                  />
                )}
                {descDraft === null ? (
                  <p
                    className={`pm-template-desc-display${template.description ? '' : ' pm-template-desc-empty'}`}
                    title="Click to edit description"
                    onClick={() => setDescDraft(template.description || '')}
                  >
                    {template.description ? linkify(template.description) : 'Add a short description…'}
                  </p>
                ) : (
                  <textarea
                    className="pm-template-desc-input"
                    value={descDraft}
                    autoFocus
                    rows={2}
                    onChange={(e) => setDescDraft(e.target.value)}
                    onBlur={commitDescription}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setDescDraft(null);
                    }}
                  />
                )}
              </div>
              <div className="pm-header-actions">
                <button
                  type="button"
                  className="pm-button pm-button-primary"
                  onClick={() => setUseOpen(true)}
                >
                  Use template
                </button>
                <button
                  type="button"
                  className="pm-button pm-button-danger"
                  onClick={handleDeleteTemplate}
                >
                  Delete template
                </button>
              </div>
            </div>

            {error ? <div className="pm-error">{error}</div> : null}

            {templateWorkstreams.length === 0 ? (
              <div className="pm-empty">
                <h3>No workstreams yet</h3>
                <p>Add one below to start scaffolding tasks.</p>
              </div>
            ) : (
              <div className="pm-template-workstreams">
                {templateWorkstreams.map((ws) => {
                  const wsTasks = tasksByWs[ws.id] || [];
                  return (
                    <section key={ws.id} className="pm-template-ws">
                      <header className="pm-template-ws-header">
                        <input
                          className="pm-template-ws-title"
                          defaultValue={ws.title}
                          onBlur={(e) => handleRenameWorkstream(ws, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                          }}
                        />
                        <span className="pm-template-ws-count">{wsTasks.length}</span>
                        <button
                          type="button"
                          className="pm-link-button pm-danger pm-template-ws-delete"
                          onClick={() => handleDeleteWorkstream(ws)}
                          title="Delete workstream"
                        >
                          Delete
                        </button>
                      </header>

                      {wsTasks.length === 0 ? (
                        <div className="pm-template-task-empty">No tasks yet.</div>
                      ) : (
                        <ul className="pm-template-tasks">
                          {wsTasks.map((task) => (
                            <li key={task.id} className="pm-template-task">
                              <input
                                className="pm-template-task-label"
                                defaultValue={task.label}
                                onBlur={(e) => handleRenameTask(task, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                }}
                              />
                              <input
                                className="pm-template-task-desc"
                                defaultValue={task.description || ''}
                                placeholder="Description (optional)"
                                onBlur={(e) => handleEditTaskDescription(task, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                }}
                              />
                              <label className="pm-template-task-offset" title="Due offset in days after project start. Blank = unscheduled.">
                                <span>+</span>
                                <input
                                  type="number"
                                  inputMode="numeric"
                                  defaultValue={typeof task.dueOffsetDays === 'number' ? task.dueOffsetDays : ''}
                                  placeholder=""
                                  onBlur={(e) => handleChangeOffset(task, e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                  }}
                                />
                                <span>d</span>
                              </label>
                              <button
                                type="button"
                                className="pm-link-button pm-danger pm-template-task-delete"
                                onClick={() => handleDeleteTask(task)}
                                title="Delete task"
                              >
                                ×
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}

                      <form
                        className="pm-template-add-task"
                        onSubmit={(e) => { e.preventDefault(); handleAddTask(ws); }}
                      >
                        <input
                          placeholder="New task"
                          value={newTaskByWs[ws.id] || ''}
                          onChange={(e) =>
                            setNewTaskByWs((prev) => ({ ...prev, [ws.id]: e.target.value }))
                          }
                        />
                        <button
                          type="submit"
                          className="pm-button"
                          disabled={!(newTaskByWs[ws.id] || '').trim()}
                        >
                          Add
                        </button>
                      </form>
                    </section>
                  );
                })}
              </div>
            )}

            <form
              className="pm-create-form"
              onSubmit={handleAddWorkstream}
              style={{ marginTop: 12 }}
            >
              <input
                placeholder="New workstream title"
                value={newWorkstreamTitle}
                onChange={(e) => setNewWorkstreamTitle(e.target.value)}
              />
              <div>
                <button
                  className="pm-button pm-button-primary"
                  type="submit"
                  disabled={addingWs || !newWorkstreamTitle.trim()}
                >
              {addingWs ? 'Adding…' : 'Add workstream'}
            </button>
          </div>
        </form>
      </div>

      <UseTemplateModal
        open={useOpen}
        template={template}
        workstreams={workstreams}
        tasks={tasks}
        busy={useBusy}
        error={useError}
        onCancel={() => { if (!useBusy) { setUseOpen(false); setUseError(null); } }}
        onSubmit={handleUseSubmit}
      />
    </PmSuiteLayout>
  );
};

export default TemplateDetail;
